// src/components/ModalInscription.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { withReferralQuery } from "../utils/referral";

/* ---------- Icônes SVG ---------- */
function IconClose({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M6 6l12 12M18 6l-12 12" strokeLinecap="round" />
    </svg>
  );
}
function IconUser({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20a8 8 0 0 1 16 0" />
    </svg>
  );
}
function IconHome({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
    </svg>
  );
}
function IconShop({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M3 7h18l-2 12H5L3 7Z" />
      <path d="M8 7V4h8v3" />
    </svg>
  );
}
function IconManager({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="7" r="4" />
      <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2" />
      <circle cx="5" cy="9" r="2.5" />
      <circle cx="19" cy="9" r="2.5" />
    </svg>
  );
}

/* ---------- Composant ---------- */
export default function ModalInscription({ open, onClose }) {
  const [agreed, setAgreed] = useState(false);
  const reduce = useReducedMotion();
  const nav = useNavigate();
  const firstBtnRef = useRef(null);
  const titleId = "modal-inscription-title";
  const descId = "modal-inscription-desc";

  // Réinitialisation du consentement à l'ouverture/fermeture
  useEffect(() => {
    if (!open) setAgreed(false);
  }, [open]);

  // Fermeture ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus initial sur 1er bouton si consenti
  useEffect(() => {
    if (open && agreed) {
      const t = setTimeout(() => firstBtnRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, agreed]);

  const go = (path) => {
    if (!agreed) return;
    onClose?.();
    nav(withReferralQuery(path));
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: reduce ? 0 : 0.2 } },
    exit: { opacity: 0, transition: { duration: reduce ? 0 : 0.15 } },
  };

  const panelVariants = {
    hidden: { opacity: 0, y: 16, scale: 0.98 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: reduce ? 0 : 0.28, ease: "easeOut" } },
    exit: { opacity: 0, y: 10, scale: 0.98, transition: { duration: reduce ? 0 : 0.18, ease: "easeIn" } },
  };

  const listVariants = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.05, delayChildren: reduce ? 0 : 0.05 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 8 },
    show: { opacity: 1, y: 0, transition: { duration: reduce ? 0 : 0.25, ease: "easeOut" } },
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descId}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            variants={backdropVariants}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            variants={panelVariants}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-2">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Fermer"
              >
                <IconClose />
              </button>

              <h2 id={titleId} className="text-xl font-bold text-center text-blue-700">
                Créer un compte
              </h2>
              <p id={descId} className="text-sm text-gray-600 text-center mt-2">
                Sélectionnez votre profil pour continuer
              </p>
            </div>

            {/* Consentement Section */}
            <div className="px-6 py-4">
              <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-xs text-gray-600 leading-snug">
                  En cochant cette case, j'accepte la collecte de mes données personnelles nécessaire à la création et à la gestion de mon compte.
                </span>
              </label>
            </div>

            {/* Options */}
            <motion.div
              className={`px-6 pb-6 pt-2 transition-all duration-300 ${
                !agreed ? "opacity-40 grayscale-[0.5] pointer-events-none select-none" : "opacity-100"
              }`}
              variants={listVariants}
              initial="hidden"
              animate="show"
            >
              <motion.button
                ref={firstBtnRef}
                variants={itemVariants}
                onClick={() => go("/inscription-locataire")}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-800 flex items-center justify-between transition"
              >
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-700">
                    <IconUser />
                  </span>
                  Locataire
                </span>
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>

              <motion.button
                variants={itemVariants}
                onClick={() => go("/inscription-proprietaire")}
                className="mt-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-800 flex items-center justify-between transition"
              >
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-700">
                    <IconHome />
                  </span>
                  Propriétaire
                </span>
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>

              <motion.button
                variants={itemVariants}
                onClick={() => go("/inscription-vendeur")}
                className="mt-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-800 flex items-center justify-between transition"
              >
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-700">
                    <IconShop />
                  </span>
                  Vendeur
                </span>
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>

              <motion.button
                variants={itemVariants}
                onClick={() => go("/inscription-gestionnaire")}
                className="mt-3 w-full px-4 py-3 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 font-medium text-gray-800 flex items-center justify-between transition"
              >
                <span className="inline-flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-green-50 text-green-700">
                    <IconManager />
                  </span>
                  Gestion locative
                </span>
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}